import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import htmlParser from "react-markdown/plugins/html-parser";
import gfm from "remark-gfm";
import cx from "classnames";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import { Link as MuiLink, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import CustomButton from "./custom-button";
import "./styles.css";
import emoji from "emoji-dictionary";
// import { isMobile } from "react-device-detect";
import { navigate } from "gatsby";
import { smoothScrolling } from "./smooth-scrolling";
import "../../styles/global.css";
import ResponsiveImg from "./responsive-img";
import IPhoneImg from "../../assets/images/iPhone 15 Pro Max - Natural Titanium - Portrait.png";

const emojiSupport = text => text.value.replace(/:\w+:/gi, name => emoji.getUnicode(name));


const styles = (theme) => ({
  rootContainer: {
    gap: "1rem",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(1%, 1fr))",
    "@media print": {
      gap: "1rem",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(1%, 1fr))",
    },
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexWrap: "wrap",
    },
  },
  root: {
    width: "100%",

    "@media print": {
      fontSize: ".725rem",
    },
  },
  listItem: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "start",
  },
  listItemText: {
    lineHeight: 1.4,
    "@media print": {
      fontSize: ".725rem",
    },
  },
  listMarker: {
    width: 5,
    height: 5,
    minWidth: 5,
    minHeight: 5,
    maxWidth: 5,
    maxHeight: 5,
    borderRadius: 5,
    backgroundColor: "gray",
    marginRight: 20,
    marginTop: "calc((1rem * 1.4 - 5px) / 2)", // (font_size * line_height - dot_height) / 2
  },
  header: {
    marginTop: 10,
    position: "relative",

    "@media print": {
      fontSize: "1.25rem",
    },
  },
  headerBlue: {
    color: theme.palette.primary.main,
  },
  button: {
    margin: "16px 0",

    "@media print": {
      display: "none",
    },
  },
  secondaryButton: {
    boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
  },
  typography: {
    marginBottom: 20,
    whiteSpace: "break-spaces",
    display: "block",
    "& img": {
      float: "left",
      margin: 20,
      marginTop: 6,
      marginLeft: 0,
      marginBottom: 10,
      maxWidth: "50%",

      [theme.breakpoints.down(690)]: {
        maxWidth: "100%",
      },

      "@media print": {
        float: "left",
        margin: 20,
        marginTop: 6,
        marginLeft: 0,
        marginBottom: 10,
        maxWidth: "50%",
        maxHeight: 400,
      },
    },

    "@media print": {
      fontSize: ".725rem",
    },
  },
  typographyFlex: {
    display: "grid",
    "& img": {
      maxWidth: "100%",

      "@media print": {
        maxWidth: "100%",
        maxHeight: 400,
      },
    },

    "@media print": {
      fontSize: ".725rem",
    },
  },
  link: {
    color: theme.colors.orange,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "inherit",
    textAlign: "left",
    verticalAlign: "baseline",

    "&:hover": {
      textDecoration: "underline",
    },

    "@media print": {
      fontSize: ".725rem",
      color: "#5d5d5d",
    },
  },
  prePrimary: {
    borderColor: "#8EB7E8",
    backgroundColor: "#E6EFFA",
  },
  table: {
    boxShadow: "none",
    border: "none",
    borderRadius: 10,
    overflow: "hidden",

    "@media print": {
      fontSize: ".725rem",
    },
  },
  tableContainer: {
    border: "1px solid #D8D8D8",
    borderRadius: 10,
  },
  tableTitle: {
    margin: 0,
    padding: "15px 20px",
    color: theme.palette.primary.main,
    fontSize: "1.3rem",
    fontWeight: 600,
    borderBottom: "1px solid #D8D8D8",

    "@media print": {
      fontSize: ".725rem",
    },
  },
  tableRow: {
    borderTop: "1px solid #D8D8D8",
    "&:first-child": {
      borderTop: 0,
    },

    "&:last-child": {
      borderBottom: 0,
    },
  },
  tableBody: {
    fontSize: 50,
    "@media print": {
      fontSize: ".725rem",
    },
  },
  tableSell: {
    height: "2.5rem",
    borderRight: "1px solid #D8D8D8",
    borderBottom: 0,

    "&:last-child": {
      borderRight: 0,
    },

    "& .MuiTypography-body1": {
      "@media print": {
        fontSize: ".725rem",
      },
    },
  },
  tableHead: {
    borderBottom: "1px solid #D8D8D8",

    "& p": {
      textAlign: "center",
      fontWeight: 600,
      textTransform: "uppercase",
      fontSize: ".7rem",
      color: theme.palette.text.primary,
    },

    "@media print": {
      fontSize: ".725rem",
    },
  },
  linkBiding: {
    position: "absolute",
    top: -120,
  },
  videoContainer: {
    position: "relative",
    width: "100%",
    height: "auto",
    marginBottom: 30,
    marginTop: 30,
    display: "flex",
    justifyContent: "center",
  },
  youtubeVideoContainer: {
    position: "relative",
    width: "100%",
    aspectRatio: "2 / 1",
    backgroundColor: "white",
  },
  urlVideoContainer: {
    position: "relative",
    width: "100%",
    aspectRatio: "2 / 1",
    backgroundColor: "white",
  },
  youtubeVideo: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    aspectRatio: "2 / 1",
    backgroundColor: "white",
  },
  urlVideo: {
    width: "100%",
    height: "100%",
    backgroundColor: "white",
  },

  "@media print": {
    fontSize: ".725rem",
  },

  slateEditingWrapper: {
    position: "relative",
    aspectRatio: .55,
  },
  slateIframe: {
    borderRadius: "8%",
    top: "4.6%",
    left: "7%",
    width: "86%",
    height: "93%",
    position: "absolute",
  },
  iPhoneImage: {
    position: "absolute",
    height: "100%",
    width: "100% !important",
    maxWidth: "100% !important",
    pointerEvents: "none",
  },
});

const redirectToNewUrl = (url) => navigate(url);

const tryOpenInNewTab = (url) => {
  const newWin = window && window.open(url);

  if (!newWin || newWin.closed || typeof newWin.closed == "undefined") {
    redirectToNewUrl(url);
  }
};

const onScrollToForm = (e) => {
  const element = document.getElementById("customForm");
  typeof window !== "undefined" && window.gtag && window.gtag("event", "click", {
    category: "Form Shortcut",
    label: "page slug",
  });
  smoothScrolling(element);
  e.preventDefault();
};

const openLink = (event, href, googleAnalyticsKey) => {
  if (googleAnalyticsKey) {
    typeof window !== "undefined" && window.gtag && window.gtag("event", "click", {
      category: googleAnalyticsKey,
      label: "page slug",
    });
  }

  const shouldOpenNewTab = href.includes("/:newTab");
  const shouldItRedirect = href.includes("/:redirect");
  const url = href.replace("/:redirect", "").replace("/:newTab", "");

  const mainContent = document.getElementById("mainContent");
  const rootContainer = document.getElementById("mainContentWrapper");

  const loader = document.createElement("div");
  loader.id = "url-loader";
  loader.classList.add("lds-dual-ring");
  loader.classList.add("link-loader");

  rootContainer.classList.add("show-loader");
  mainContent.appendChild(loader);

  setTimeout(() => {
    loader.classList.add("link-loader-opacity");
  }, 0);

  const removeLoader = () => setTimeout(() => {
    loader.remove();
    rootContainer.classList.remove("show-loader");
  }, 2000);

  if (shouldOpenNewTab) {
    event.preventDefault();
  }

  if (shouldItRedirect) {
    fetch(url)
      .then(response => response.text())
      .then(result => {
        if (shouldOpenNewTab) {
          removeLoader();
          tryOpenInNewTab(result);
        } else {
          redirectToNewUrl(result);
          removeLoader();
        }
      })
      .catch(function(err) {
        console.info(err);
        loader.remove();
        rootContainer.classList.remove("show-loader");
      });
  } else {
    if (shouldOpenNewTab) {
      removeLoader();
      tryOpenInNewTab(url);
    } else {
      redirectToNewUrl(url);
      removeLoader();
    }
  }
};

const MarkdownParagraph = withStyles(styles)(({ classes, ...props }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const window_width = document?.documentElement?.clientWidth||
      document?.body?.clientWidth || 0;
    setIsMobile(window_width < 500)
  }, []);

  const regexIsItButton = /\[[^\]]*]\([^)]*\)/gmi;
  const regexTextAlign = /\.\..*\.\./gmi;
  let content = [...props.children];
  const currentItem = content[0].props.children;
  let textAlign = "left";
  let isInline = true;

  //------ check whether the text is specially aligned ---------
  if ((typeof currentItem) === "string" &&
    currentItem.match(regexTextAlign)
  ) {
    textAlign = currentItem.split("..")[1];
  }

  //------ if specially text align exist => delete text align string from context ---------
  if (textAlign !== "left") {
    const cutTextAlignNote = {
      ...content[0], props: {
        ...content[0].props,
        value: currentItem.replace(regexTextAlign, ""),
        children: currentItem.replace(regexTextAlign, ""),
        node: {
          ...content[0].props.node,
          value: currentItem.replace(regexTextAlign, ""),
        },
      },
    };
    content.splice(0, 1);
    content = [cutTextAlignNote, ...content];
  }


  //------ check is it button or image ---------
  content = content.map(item => {

    //------ check is it image ---------
    if (item.type === "img") {
      isInline = item.props.alt.startsWith("!");
      let str = item.props.alt.startsWith("!") ? item.props.alt.substr(1) : item.props.alt;
      let findWidth = item.props.src.split("/width=");
      const searchParams = new URLSearchParams(item.props.src.substring(item.props.src.indexOf("?")));
      const width = searchParams.get("width");
      const align = searchParams.get("align");

      const props = {
        ...item.props,
        alt: str,
        src: findWidth[0],
      };

      if (str.length > 1) {
        props.style = {
          maxWidth: isMobile ? "100%" : findWidth[1] ?? width,
          backgroundColor: "white",
          float: align ?? "left",
          justifySelf: align,
          marginBottom: isMobile ? 40 : 20,
          padding:isMobile ? '0 5%' : null,
          marginLeft: align === "center" || align === "right" ? 20 : 0,
          marginRight: align === "center" || align === "left" || !align ? 20 : 0,
        };
      }

      if (findWidth[0].startsWith("sl8.link/")) {
        isInline = true;
        const productionUrl = "https://slate.slatepages.com/#/";
        const developmentUrl = "https://slate.dev.slatepages.com/#/";
        // const iframeInitSrc = "http://localhost:5555/#/";
        const iframeInitSrc = process.env.GATSBY_ISDEVELOPMENT === `true`
          ? developmentUrl : productionUrl;
        const slateUuid = findWidth[0].split("/")[1].split("?")[0];
        const width = props.maxWidth ?? 450;

        return <div style={{ ...props.style, maxWidth: "100%", width: width, marginRight: 0 }}
                    className={classes.slateEditingWrapper}>
          <iframe
            className={classes.slateIframe}
            id="flutter_widget"
            title="Slate Pages Preview"
            src={`${iframeInitSrc}slate_details?stackUuid=_&slateType=slate&uuid=${slateUuid}&shouldClearSlateStack=true&imageUrl=null&isDisableEditingAllField=false&isDemoMode=true`}
            frameBorder="0"
            allow="geolocation; accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            scrolling="yes"
          />
          <ResponsiveImg
            src={IPhoneImg}
            alt="Slate Pages"
            className={classes.iPhoneImage}
          />
        </div>;
      }

      return { ...item, props };
    }

    //------ check is it button ---------
    if ((typeof item.props.children) === "string" &&
      item.props.children.match(regexIsItButton)
    ) {
      let buttons = item.props.children.match(regexIsItButton).map(button => {
        const context = button.split("]");
        let text = context[1].substr(1).slice(0, -1);
        let href = context[0].substr(2);
        let primaryButton = true;

        if (href.startsWith("!")) {
          primaryButton = false;
          href = href.substr(1);
        }

        let googleAnalyticsKey;
        if (text.startsWith("GAKey:")) {
          const arr = text.split(/ (.*)/);
          googleAnalyticsKey = arr[0].replace("GAKey:", "");

          text = arr[1];
        }

        if (href === "/scrollToForm") {
          return <CustomButton variant="contained"
                               color={primaryButton ? "primary" : "secondary"} mb={3}
                               className={cx(classes.button,
                                 { [classes.secondaryButton]: !primaryButton },
                               )}
                               onClick={onScrollToForm}
          >
            {text}
          </CustomButton>;
        } else {
          return <CustomButton variant="contained"
                               color={primaryButton ? "primary" : "secondary"} mb={3}
                               className={cx(classes.button,
                                 { [classes.secondaryButton]: !primaryButton },
                               )}
                               onClick={(e => openLink(e, href, googleAnalyticsKey))}
          >
            {text}
          </CustomButton>;
        }
      });

      return item.props.children
        .split(regexIsItButton)
        .map((item, index) => {
          return [item, buttons[index] || null];
        })
        .flat();
    }
    return item;
  });
  return <Typography className={cx(classes.typography, { [classes.typographyFlex]: !isInline })} align={textAlign}>
    {content}
  </Typography>;
});

const MarkdownHeading = withStyles(styles)(({ classes, ...props }) => {
  let variant;
  switch (props.level) {
    case 1:
      variant = "h2";
      break;
    case 2:
      variant = "h4";
      break;
    case 3:
      variant = "h6";
      break;
    case 4:
      variant = "subtitle1";
      break;
    case 5:
      variant = "subtitle2";
      break;
    case 6:
      variant = "subtitle2";
      break;
    default:
      variant = "h6";
      break;
  }
  const regexTextAlign = /\.\..*\.\./gmi;
  let currentItem = props.children[0].props.children;
  if (currentItem.startsWith("!")) {
    currentItem = currentItem.substr(1);
  }
  const textAlign = currentItem.match(regexTextAlign) && currentItem.split("..")[1];
  const content = textAlign ? currentItem.replace(regexTextAlign, "") : currentItem;

  return <Typography className={cx(
    classes.header,
    {
      [classes.headerBlue]: props.level === 6,
    })}
                     gutterBottom
                     variant={variant}
                     align={textAlign || "left"}
  >
    {textAlign ? currentItem.replace(regexTextAlign, "") : currentItem}
    <div id={content.trim()} className={classes.linkBiding} />
  </Typography>;
});

const MarkdownListItem = withStyles(styles)(({ classes, ...props }) => {
  return (
    <li className={classes.listItem}>
      <div className={classes.listMarker} />
      <Typography component="span" className={classes.listItemText}>{props.children}</Typography>
    </li>
  );
});

const MarkdownList = withStyles(styles)(({ classes, ...props }) => {
  return (
    <ul style={{ paddingLeft: 20 }}>
      {props.children}
    </ul>
  );
});

const MarkdownTable = withStyles(styles)(({ classes, ...props }) => {
  return (
    <div className={classes.tableContainer}>
      <TableContainer component={Paper} className={classes.table}>
        <Table size="small" aria-label="a dense table">{props.children}</Table>
      </TableContainer>
    </div>
  );
});

const MarkdownTableCell = withStyles(styles)(({ classes, ...props }) => {
  return (
    <TableCell className={classes.tableSell}><Typography>{props.children}</Typography></TableCell>
  );
});

const MarkdownTableRow = withStyles(styles)(({ classes, ...props }) => {
  return (
    <TableRow className={classes.tableRow}>{props.children}</TableRow>
  );
});

const MarkdownTableBody = withStyles(styles)(({ classes, ...props }) => {
  return <TableBody className={classes.tableBody}>{props.children}</TableBody>;
});

const MarkdownTableHead = withStyles(styles)(({ classes, ...props }) => {
  return (
    <TableHead className={classes.tableHead}>{props.children}</TableHead>
  );
});

const MarkdownLink = withStyles(styles)(({ classes, ...props }) => {
  const { href, children } = props;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const window_width = document?.documentElement?.clientWidth||
      document?.body?.clientWidth || 0;
    setIsMobile(window_width < 500)
  }, []);


  let content = <MuiLink
    underline="none"
    onClick={(e => openLink(e, href))}
    tabIndex={0}
    component="button"
    className={classes.link}
  >
    {children}
  </MuiLink>;

  if (children[0].props.src === "URL_VIDEO" || children[0].props.src === "YOUTUBE_VIDEO") {
    const isYouTubeVideo = children[0].props.src === "YOUTUBE_VIDEO";
    const styles = children[0].props.alt ? children[0].props.alt.split(",") : [];
    const width = styles[0] && !isMobile ? (styles[0] + "%") : "100%";
    const aspectRatio = styles[1];
    content =
      <div
        className={classes.videoContainer}
      >
        <div
          className={classes.urlVideoContainer}
          style={{
            width: width,
            aspectRatio: aspectRatio?.toString() ?? "2 / 1",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              aspectRatio: aspectRatio?.toString() ?? "2 / 1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <iframe
              style={{ background: "#FFFFFF;" }}
              className={classes.urlVideo}
              title="Video content"
              data-code={href}
              src={isYouTubeVideo ? `https://www.youtube.com/embed/${href}?autoplay=1` : href + `?autoplay=1`}
              data-src={isYouTubeVideo ? `https://www.youtube.com/embed/${href}?autoplay=1` : href + `?autoplay=1&transparent=0`}
              frameBorder="0"
              allowFullScreen="true"
            />
          </div>
        </div>
      </div>;
  }

  //------ check is it button ---------
  if (children[0].props.node && children[0].props.node.value && children[0].props.node.value.startsWith("!")) {
    let text = children[0].props.node.value.substr(1);
    let primaryButton = true;
    if (text.startsWith("!")) {
      primaryButton = false;
      text = text.substr(1);
    }

    let googleAnalyticsKey;
    if (text.startsWith("GAKey:")) {
      const arr = text.split(/ (.*)/);
      googleAnalyticsKey = arr[0].replace("GAKey:", "");

      text = arr[1];
    }

    if (href === "/scrollToForm") {
      content = <CustomButton variant="contained"
                              color={primaryButton ? "primary" : "secondary"} mb={3}
                              className={cx(classes.button,
                                { [classes.secondaryButton]: !primaryButton },
                              )}
                              onClick={onScrollToForm}
      >
        {text}
      </CustomButton>;
    } else {
      content = <CustomButton variant="contained"
                              color={primaryButton ? "primary" : "secondary"} mb={3}
                              className={cx(classes.button,
                                { [classes.secondaryButton]: !primaryButton },
                              )}
                              onClick={(e => openLink(e, href, googleAnalyticsKey))}
      >
        {text}
      </CustomButton>;
    }
  }

  return (
    <>{content}</>
  );
});

const MarkdownCode = withStyles(styles)(({ classes, ...props }) => {
  return (
    <pre>
        <code>
        {props.value}
        </code>
      </pre>
  );
});

export const renderers = {
  text: emojiSupport,
  heading: MarkdownHeading,
  paragraph: MarkdownParagraph,
  link: MarkdownLink,
  code: MarkdownCode,
  list: MarkdownList,
  listItem: MarkdownListItem,
  table: MarkdownTable,
  tableHead: MarkdownTableHead,
  tableBody: MarkdownTableBody,
  tableRow: MarkdownTableRow,
  tableCell: MarkdownTableCell,
};

const MarkdownWrapper = withStyles(styles)(({
                                              classes,
                                              className,
                                              newChildrenCodeStyle,
                                              ...rest
                                            }) => {
  const parseHtml = htmlParser({
    // is it enough?
    isValidNode: (node) => node.type !== "script",
    // processingInstructions: [
    //   /* ... */
    // ],
  });


  return (
    <>
      {
        newChildrenCodeStyle.map((item, index) => {
          if (!(index % 2)) {
            const newChildren = item.split("--!--");

            return newChildren.map((child, index) => {
              if (!child.length) return null;
              let columnsList = child.split("-!-");

              return <div key={index} className={classes.rootContainer}>
                {
                  columnsList.map((item, index) => (
                    <ReactMarkdown
                      key={index}
                      className={cx(classes.root, className)}
                      astPlugins={[parseHtml]}
                      plugins={[gfm]}
                      allowDangerousHtml
                      escapeHtml={false}
                      renderers={renderers}
                      {...rest}
                      children={item}
                    />
                  ))
                }
              </div>;
            });
          } else {
            return <ReactMarkdown
              key={index}
              className={cx(classes.root, className)}
              astPlugins={[parseHtml]}
              plugins={[gfm]}
              allowDangerousHtml
              escapeHtml={false}
              renderers={renderers}
              {...rest}
              children={"~~~" + item + "~~~"}
            />;
          }
        })
      }
    </>
  );
});

MarkdownWrapper.propTypes = {
  className: PropTypes.string,
};

export default MarkdownWrapper;
